import { useCallback, useEffect, useState } from "react";
import { Boxes, GitBranch, ExternalLink } from "lucide-react";
import api from "../api";
import { fmt, expiryLabel } from "../utils";
import { useLang } from "../i18n";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const filters = [
  { key: "", label: "All Batches" },
  { key: "instock", label: "In Stock" },
  { key: "near_expiry", label: "Near Expiry" },
  { key: "expired", label: "Expired" },
];

export default function Inventory({ initialStatus = "instock", embedded = false }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(initialStatus);
  const [trace, setTrace] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const { t } = useLang();

  const load = useCallback(async () => {
    const { data } = await api.get("/batches", { params: { status } });
    setRows(data);
  }, [status]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const openTrace = async (batch) => {
    setTraceLoading(true);
    try {
      const { data } = await api.get(`/batches/${batch.id}/trace`);
      setTrace(data);
    } catch (err) {
      toast.error(err.message || "Stock flow load nahi ho paaya");
    } finally {
      setTraceLoading(false);
    }
  };

  const totalValue = rows.reduce((s, b) => s + b.qty * b.purchase_rate, 0);

  return (
    <div data-testid="inventory-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        {!embedded && (
          <div>
            <h1 className="sgp-page-title">{t("inventory")}</h1>
            <p className="sgp-page-subtitle">Purchase Bills se automatically bana hua batch-wise live stock record.</p>
          </div>
        )}
        <div data-testid="inventory-total-value" className={`sgp-card px-4 py-2 text-sm ${embedded ? "ml-auto" : ""}`}>
          <span className="text-slate-500 text-xs uppercase tracking-wider">Stock Value: </span>
          <span className="font-mono font-bold text-teal-700">{fmt(totalValue)}</span>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button key={f.key} data-testid={`filter-${f.key || "all"}`} onClick={() => setStatus(f.key)}
            className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${
              status === f.key ? "bg-teal-700 border-teal-700 text-white" : "border-slate-300 text-slate-600 hover:border-teal-600"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[980px]">
          <thead>
            <tr>
              <th className="sgp-th">Medicine</th><th className="sgp-th">Batch No.</th><th className="sgp-th">Manufactured</th><th className="sgp-th">Expiry</th>
              <th className="sgp-th text-right">Stock Qty</th><th className="sgp-th text-right">Purchase Rate</th>
              <th className="sgp-th text-right">Stock Value</th><th className="sgp-th">Source</th><th className="sgp-th">Status</th><th className="sgp-th">Flow</th>
            </tr>
          </thead>
          <tbody data-testid="inventory-table">
            {rows.map((b) => (
              <tr key={b.id} data-testid={`batch-row-${b.id}`}
                className={b.expired ? "bg-rose-50/60" : b.near_expiry ? "bg-amber-50/60" : "hover:bg-slate-50/60"}>
                <td className="sgp-td font-semibold text-slate-800">{b.item_name}</td>
                <td className="sgp-td font-mono text-xs">{b.batch_no}</td>
                <td className="sgp-td font-mono text-xs">{b.manufacture_date || "—"}</td>
                <td className="sgp-td font-mono text-xs">{expiryLabel(b.expiry)}</td>
                <td className="sgp-td text-right font-mono font-semibold">{b.qty} <span className="text-[10px] text-slate-400 font-normal">{b.unit}</span></td>
                <td className="sgp-td text-right font-mono text-xs">{fmt(b.purchase_rate)}</td>
                <td className="sgp-td text-right font-mono text-xs">{fmt(b.qty * b.purchase_rate)}</td>
                <td className="sgp-td text-xs text-slate-500">{b.purchase_id ? "Purchase" : b.source === "manual_opening_stock" ? "Manual / Opening" : "—"}</td>
                <td className="sgp-td">
                  {b.expired
                    ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Expired</span>
                    : b.near_expiry
                      ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Expiring Soon</span>
                      : b.qty > 0
                        ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">In Stock</span>
                        : <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Sold Out</span>}
                </td>
                <td className="sgp-td">
                  <button type="button" data-testid={`batch-trace-${b.id}`} onClick={() => openTrace(b)} disabled={traceLoading}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                    <GitBranch className="h-3.5 w-3.5" /> Trace
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={10} className="sgp-td text-center py-10 text-slate-400">
                <Boxes className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                Is filter mein koi batch nahi. New Purchase Bill save karte hi batch aur stock yahan automatically aa jayega.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!trace} onOpenChange={(open) => { if (!open) setTrace(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="batch-trace-dialog">
          <DialogHeader><DialogTitle className="font-heading">Stock Flow — {trace?.item?.name || "Batch"}</DialogTitle></DialogHeader>
          {trace && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div><p className="text-[10px] uppercase font-bold text-slate-500">Batch</p><p className="font-mono font-semibold">{trace.batch.batch_no}</p></div>
                <div><p className="text-[10px] uppercase font-bold text-slate-500">Current Stock</p><p className="font-mono font-semibold">{trace.batch.qty} {trace.item.unit || ""}</p></div>
                <div><p className="text-[10px] uppercase font-bold text-slate-500">Expiry</p><p className="font-mono font-semibold">{trace.batch.expiry || "—"}</p></div>
                <div><p className="text-[10px] uppercase font-bold text-slate-500">Workspace</p><p className="font-semibold">{trace.workspace?.workspace_company_name} · {trace.workspace?.financial_year}</p></div>
              </div>

              <section>
                <h3 className="font-bold text-slate-800 mb-2">1. Stock came from</h3>
                {trace.purchase ? (
                  <Link target="_blank" rel="noreferrer" to={`/print/purchase/${trace.purchase.id}`}
                    className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 p-3 hover:border-emerald-400">
                    <span><b>{trace.purchase.bill_no}</b> · {trace.purchase.company_name} · {trace.purchase.date}</span>
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                ) : trace.purchase_restricted ? (
                  <p className="rounded-lg border border-slate-200 p-3 text-slate-500">Purchase source linked hai, lekin aapke role ko purchase details ka access nahi hai.</p>
                ) : <p className="rounded-lg border border-slate-200 p-3 text-slate-500">Manual / opening stock source.</p>}
              </section>

              <section>
                <h3 className="font-bold text-slate-800 mb-2">2. Stock used in Sales Bills</h3>
                <div className="space-y-2">
                  {trace.sales?.map((row) => (
                    <Link key={row.id} target="_blank" rel="noreferrer" to={`/print/sale/${row.id}`}
                      className="flex items-center justify-between rounded-lg border border-slate-200 p-3 hover:border-indigo-300">
                      <span><b>{row.bill_no}</b> · {row.party} · Qty {row.qty}</span><ExternalLink className="h-4 w-4" />
                    </Link>
                  ))}
                  {!trace.sales?.length && <p className="text-slate-500">Is batch se abhi direct sales bill nahi bana.</p>}
                </div>
              </section>

              {!!trace.challans?.length && (
                <section>
                  <h3 className="font-bold text-slate-800 mb-2">3. Delivery Challans</h3>
                  <div className="space-y-2">{trace.challans.map((row) => (
                    <Link key={row.id} target="_blank" rel="noreferrer" to={`/print/challan/${row.id}`}
                      className="flex items-center justify-between rounded-lg border border-slate-200 p-3 hover:border-amber-300">
                      <span><b>{row.bill_no}</b> · {row.party} · Qty {row.qty} · {row.status}</span><ExternalLink className="h-4 w-4" />
                    </Link>
                  ))}</div>
                </section>
              )}

              <section>
                <h3 className="font-bold text-slate-800 mb-2">4. Returns / Stock Reversal</h3>
                <div className="space-y-2">
                  {trace.returns?.map((row) => (
                    <Link key={row.id} target="_blank" rel="noreferrer" to={`/print/${row.kind === "sale" ? "credit_note" : "debit_note"}/${row.id}`}
                      className="flex items-center justify-between rounded-lg border border-slate-200 p-3 hover:border-rose-300">
                      <span><b>{row.ret_no}</b> · {row.kind === "sale" ? "Sales Return" : "Purchase Return"} · Qty {row.qty}</span><ExternalLink className="h-4 w-4" />
                    </Link>
                  ))}
                  {!trace.returns?.length && <p className="text-slate-500">Is batch par koi return movement nahi.</p>}
                </div>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
